import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { SessionService } from '../services/session.service';
import { AuthService } from '../services/auth.service';
import { IdleTimeoutService } from '../services/idle-timeout.service';

// Renovación proactiva: si el access token tiene más de 45 min (vive 1 hora),
// se renueva ANTES de enviar la petición, mientras el usuario esté activo.
const RENEW_THRESHOLD_MS = 45 * 60 * 1000;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const sessionService = inject(SessionService);
    const idleTimeout = inject(IdleTimeoutService);

    const publicUrls = ['/auth/login', '/auth/refresh-token', '/auth/logout', '/auth/forgot'];
    const isPublicUrl = publicUrls.some((url) => req.url.includes(url));

    if (isPublicUrl) {
        return next(req);
    }

    // Toda petición a la API cuenta como actividad del usuario
    idleTimeout.updateActivity();

    const token = sessionService.getToken();

    if (!token) {
        return next(req);
    }

    const send = () =>
        next(
            req.clone({
                setHeaders: { Authorization: `Bearer ${sessionService.getToken()}` }
            })
        );

    // Renovación proactiva si el token está próximo a vencer
    const savedAt = sessionService.getTokenSavedAt();
    if (savedAt && Date.now() - savedAt > RENEW_THRESHOLD_MS) {
        return authService.refreshTokens().pipe(
            switchMap(() => send()),
            catchError((error: HttpErrorResponse) => {
                // Si la renovación falló, clearAndRedirect ya manejó el logout
                return throwError(() => error);
            })
        );
    }

    return send().pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !req.url.includes('/auth/refresh-token')) {
                return authService.refreshTokens().pipe(
                    switchMap(() => send()),
                    catchError((refreshError) => {
                        authService.logout();
                        return throwError(() => refreshError);
                    })
                );
            }
            return throwError(() => error);
        })
    );
};
